import { msg, plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  apiGetAlertsMedicalWaitingRoom,
  MedicalWaitingRoomAlerts,
  PatientMonitoringCallStatus,
  PatientMonitoringReportStatus,
  PermissionBit,
} from "../../api";
import { AppModel } from "../../app/app-model";
import { ErrorPage } from "../../app/error-page";
import { PageHeaderActions } from "../../app/layout/layout-context";
import { useCountries } from "../../catalogs/countries";
import {
  AssignedOnlyToggle,
  useAssignedOnly,
} from "../../components/assigned-only-toggle";
import { CellEvents } from "../../components/monitoring-rooms/cell-events";
import { CellNestedAlerts } from "../../components/monitoring-rooms/cell-nested-alerts";
import { moreThanZeroLabel } from "../../components/monitoring-rooms/constants";
import { MonitoringUserBubbles } from "../../components/monitoring-users/monitoring-user-bubbles";
import { monitoringUserFilter } from "../../components/monitoring-users/monitoring-user-filter";
import { useMonitoringUsersDirectory } from "../../components/monitoring-users/use-monitoring-users-directory";
import { PatientTableSearch } from "../../components/patient-search-palette";
import { QuickLookPerLevel } from "../../components/quick-look/quick-look-button";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import { CellEnum } from "../../components/ui-kit/tables/cells/cell-enum";
import {
  type Column,
  DataTable,
} from "../../components/ui-kit/tables/data-table";
import {
  patientStates,
  watchKinds,
  watchStatuses,
} from "../../constants/enum-to-text";
import { hasMonitoringAccess } from "../../constants/permissions";
import { DataModel } from "../../models/data-model";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
  uniqueValueOptions,
} from "../../utils/filter-presets";
import { physicianName } from "../../utils/physician";
import { useAbortableFetch } from "../../utils/use-abortable-fetch";
import {
  bulkUpdatePatients,
  PatientBody,
} from "../waiting-room/bulk-update-patients";

export const MedWaitingRoomRoute = observer(function MedWaitingRoomRoute({
  app,
}: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const countries = useCountries();
  const [model] = useState(() => {
    const m = new DataModel<TableItem>(
      [],
      app.seerlinqApi.permissions,
      app.seerlinqApi.requireUserUUID(),
    );
    m.idAttr = "patient_id";
    return m;
  });

  const [assignedOnly, setAssignedOnly] = useAssignedOnly("med-waiting-room");
  const monitoringUsers = useMonitoringUsersDirectory(app.seerlinqApi);

  const {
    loading,
    error,
    refetch: fetchData,
  } = useAbortableFetch(
    (signal) =>
      apiGetAlertsMedicalWaitingRoom({
        ...(assignedOnly ? { query: { assigned_only: true } } : {}),
        signal,
      }),
    (response) =>
      model.updateData(response.data.waiting_room.map(toMedWaitingRoomItem)),
    [assignedOnly],
  );

  const canFilterByAssignment = hasMonitoringAccess(
    app.seerlinqApi.permissions,
  );

  const columns: Column<TableItem>[] = [
    {
      header: <Trans>HeartCore ♥</Trans>,
      sticky: true,
      requiredPermission: PermissionBit.RUN_HEART_CORE_QUICKLOOK,
      width: 120,
      cell: ({ item }) => (
        <QuickLookPerLevel api={app.seerlinqApi} patientId={item.patient_id} />
      ),
    },
    {
      header: <Trans>Patient ID</Trans>,
      attr: "patient_id",
      sticky: true,
      filter: {
        kind: "text",
        group: t`Identity`,
        placeholder: t`Search…`,
      },
      width: 100,
      cell: ({ item }) => (
        <strong>
          <Link to={`/patient/${item.patient_id}`}>{item.patient_id}</Link>
        </strong>
      ),
    },
    {
      header: <Trans>Name</Trans>,
      attr: "name",
      sticky: true,
      filter: {
        kind: "text",
        group: t`Identity`,
        placeholder: t`Search…`,
      },
      cell: ({ item }) => item.name,
    },
    {
      header: <Trans>Country</Trans>,
      attr: "country",
      type: "string",
      filter: {
        kind: "multi",
        group: t`Location`,
        options: getCountryOptions,
      },
    },
    {
      header: <Trans>Region</Trans>,
      attr: "region",
      editable: true,
      filter: {
        kind: "multi",
        group: t`Location`,
        options: (table) => getRegionOptions(table, countries),
        disabled: regionDisabled,
        disabledMessage: t`Select a country first`,
        predicate: regionPredicate,
      },
      cell: (props) => (
        <CellEnum
          {...props}
          attr="region"
          values={countries.getRegionsEnum(props.item.country)}
        />
      ),
    },
    {
      header: <Trans>Risk flag</Trans>,
      attr: "patient_state",
      type: "enum",
      values: patientStates,
      editable: true,
      filter: {
        kind: "multi",
        group: t`Clinical`,
        options: new Map(Object.entries(patientStates)),
      },
    },
    {
      header: <Trans>Clinical risk score</Trans>,
      attr: "clinical_risk_score",
      filter: {
        kind: "multi",
        group: t`Clinical`,
        options: uniqueValueOptions<TableItem>("clinical_risk_score"),
      },
    },
    {
      header: <Trans>Physician</Trans>,
      attr: "physician",
      type: "string",
      filter: {
        kind: "multi",
        group: t`Care team`,
        options: uniqueValueOptions<TableItem>("physician"),
      },
      cell: ({ item }) => (
        <span className="inline-flex items-center gap-1.5">
          <span>{item.physician}</span>
          <MonitoringUserBubbles
            users={monitoringUsers.usersFor(
              item.physicians?.[0]?.assigned_monitoring_users,
            )}
          />
        </span>
      ),
    },
    {
      header: <Trans>Monitoring user</Trans>,
      // The attr only provides a unique column id — the filter's predicate
      // ignores it.
      attr: "physicians",
      hidden: true,
      requiredPermission: PermissionBit.GROUP_MONITORING_ROUTERS,
      filter: monitoringUserFilter({
        resolve: (physician) =>
          monitoringUsers.usersFor(physician.assigned_monitoring_users),
        kindOf: (uuid) =>
          monitoringUsers.medicalUuids.has(uuid) ? "medical" : "technical",
      }),
    },
    {
      header: <Trans>Alerting risk score</Trans>,
      attr: "alert_risk_score",
      type: "number",
      precision: 2,
      sort: true,
      initSort: "desc",
    },
    {
      header: <Trans>LVFP</Trans>,
      attr: "lvfp_alerts",
      filter: {
        kind: "bool",
        group: t`Alerts`,
        predicate: (item) => item.lvfp_alerts.length > 0,
        toggleLabel: moreThanZeroLabel,
      },
      cell: ({ item }) => <CellNestedAlerts alerts={item.lvfp_alerts} />,
    },
    {
      header: <Trans>Vitals</Trans>,
      attr: "vitals_alerts",
      filter: {
        kind: "bool",
        group: t`Alerts`,
        predicate: (item) => item.vitals_alerts.length > 0,
        toggleLabel: moreThanZeroLabel,
      },
      cell: ({ item }) => <CellNestedAlerts alerts={item.vitals_alerts} />,
    },
    {
      header: <Trans>Symptoms</Trans>,
      attr: "symptom_alerts",
      filter: {
        kind: "bool",
        group: t`Alerts`,
        predicate: (item) => item.symptom_alerts.length > 0,
        toggleLabel: moreThanZeroLabel,
      },
      cell: ({ item }) => <CellNestedAlerts alerts={item.symptom_alerts} />,
    },
    {
      header: <Trans>SQI</Trans>,
      attr: "ppg_quality_alerts",
      filter: {
        kind: "bool",
        group: t`Alerts`,
        predicate: (item) => item.ppg_quality_alerts.length > 0,
        toggleLabel: moreThanZeroLabel,
      },
      cell: ({ item }) => <CellNestedAlerts alerts={item.ppg_quality_alerts} />,
    },
    {
      header: <Trans>Compliance alert</Trans>,
      attr: "compliance_alerts",
      filter: {
        kind: "bool",
        group: t`Alerts`,
        predicate: (item) => item.compliance_alerts.length > 0,
        toggleLabel: moreThanZeroLabel,
      },
      cell: ({ item }) => <CellNestedAlerts alerts={item.compliance_alerts} />,
    },
    {
      header: <Trans>Last events</Trans>,
      cell: ({ item }) => <CellEvents item={item} />,
    },
    {
      header: <Trans>Medical note</Trans>,
      attr: "medical_monitoring_note",
      type: "richtext",
      editable: true,
      width: 300,
    },
    {
      header: <Trans>Technical note</Trans>,
      attr: "technical_monitoring_note",
      type: "richtext",
      editable: true,
      width: 300,
    },
    {
      header: <Trans>Call status</Trans>,
      attr: "monitoring_call_status",
      type: "enum",
      values: callStatuses,
      editable: true,
      filter: {
        kind: "multi",
        group: t`Monitoring`,
        options: new Map(Object.entries(callStatuses)),
      },
    },
    {
      header: <Trans>Report status</Trans>,
      attr: "monitoring_report_status",
      type: "enum",
      values: reportStatuses,
      editable: true,
      filter: {
        kind: "multi",
        group: t`Monitoring`,
        options: new Map(Object.entries(reportStatuses)),
      },
    },
    {
      header: <Trans>Monitoring room</Trans>,
      attr: "monitoring_watch_kind",
      type: "enum",
      values: watchKinds,
      editable: true,
      filter: {
        kind: "multi",
        group: t`Monitoring`,
        options: new Map(Object.entries(watchKinds)),
      },
    },
    {
      header: <Trans>Watch status</Trans>,
      attr: "monitoring_watch_status",
      type: "enum",
      values: watchStatuses,
      editable: true,
      filter: {
        kind: "multi",
        group: t`Monitoring`,
        options: new Map(Object.entries(watchStatuses)),
      },
    },
  ];

  return (
    <>
      <PageHeaderActions>
        {canFilterByAssignment ? (
          <AssignedOnlyToggle value={assignedOnly} onChange={setAssignedOnly} />
        ) : undefined}
        <PatientTableSearch
          rows={model.data}
          patient={(item) => ({
            id: item.patient_id,
            name: item.name,
            subtitle: [
              item.patient_state != null &&
                resolveEnumLabel(patientStates[item.patient_state]),
              item.clinical_risk_score != null &&
                t`Risk ${item.clinical_risk_score}`,
              item.country,
            ],
          })}
        />
      </PageHeaderActions>
      {error ? (
        <ErrorPage
          title={t`Error loading medical waiting room`}
          message={error instanceof Error ? error.message : t`Unexpected error`}
        />
      ) : (
        <DataTable
          persistState
          itemKeyField="patient_id"
          isLoading={loading}
          filterNoun={(count) =>
            t`${plural(count, { one: "patient", other: "patients" })}`
          }
          columns={columns}
          // Reading the observable set here (observer component) also
          // rebuilds the column defs when classification loads, refreshing
          // bubble cells and filter options.
          filterDeps={[monitoringUsers.medicalUuids]}
          model={model}
          patchAlwaysIncludeKeys={[
            "monitoring_watch_kind",
            "monitoring_watch_status",
          ]}
          onMultiEdit={async (patches) => {
            const result = await bulkUpdatePatients(
              patches.map((entry) => ({
                id: entry.id,
                patch: toMedPatientBody(entry.patch),
              })),
            );
            await fetchData();
            return result;
          }}
        />
      )}
    </>
  );
});

const callStatuses = {
  [PatientMonitoringCallStatus.nothing]: msg`Nothing`,
  [PatientMonitoringCallStatus.called]: msg`Called`,
  [PatientMonitoringCallStatus.to_call]: msg`To call`,
};

const reportStatuses = {
  [PatientMonitoringReportStatus.nothing]: msg`Nothing`,
  [PatientMonitoringReportStatus.reported]: msg`Reported`,
  [PatientMonitoringReportStatus.to_report]: msg`To report`,
};

function toMedPatientBody(patch: Partial<TableItem>): PatientBody {
  if (!("monitoringOwner" in patch)) return patch as PatientBody;
  const { monitoringOwner, ...rest } = patch;
  return { ...rest, monitoring_users: [monitoringOwner] } as PatientBody;
}

export function toMedWaitingRoomItem(
  item: MedicalWaitingRoomAlerts,
): TableItem {
  const physician = physicianName(item.physicians?.[0]) ?? "";

  return {
    ...item,
    physician,
  };
}

interface TableItem extends MedicalWaitingRoomAlerts {
  physician: string;
}
